/**
 * APIManager - Centralized API utility for shared endpoints
 * Focuses on order-list and account-statement to reduce redundancy.
 */
class APIService {
    constructor(baseURL = window.AsianPaintsConfig.baseUrl) {
        this.baseURL = baseURL;
        this.inflightRequests = new Map();

        // Define page restrictions for specific endpoints
        // Key is endpoint, value is array of sub-strings that must be in location.pathname
        this.pageRestrictions = {
            'order-list': ['/', '/account', '/pages/my-account', '/pages/orders'],
            'account-statement': ['/', '/account', '/pages/my-account', '/pages/account-statement', '/pages/dashboard']
        };
    }

    /**
     * Checks if the current page is allowed to execute the given endpoint
     */
    _isPageAllowed(endpoint) {
        const allowedPaths = this.pageRestrictions[endpoint];
        if (!allowedPaths) return true; // Default to allowed if no restriction defined

        const currentPath = window.location.pathname.toLowerCase();

        // Allow if path is root, matches /account, or matches allowed substrings
        const isAllowed = allowedPaths.some(path => {
            if (path === '/') return currentPath === '/' || currentPath === '/index' || currentPath === '';
            return currentPath.includes(path.toLowerCase());
        }) || currentPath === '/account';

        if (!isAllowed) {
            console.warn(`[APIManager] API call to ${endpoint} restricted on this page (${currentPath})`);
        }
        return isAllowed;
    }

    /**
     * Generic POST helper with deduplication and page restriction
     */
    async _post(endpoint, payload) {
        if (!this._isPageAllowed(endpoint)) {
            console.warn(`[APIManager] API call to ${endpoint} restricted on this page (${window.location.pathname})`);
            return { success: false, message: 'API restricted on this page' };
        }

        const cacheKey = `${endpoint}:${JSON.stringify(payload)}`;
        if (this.inflightRequests.has(cacheKey)) {
            return this.inflightRequests.get(cacheKey);
        }

        const requestPromise = (async () => {
            try {
                const response = await fetch(`${this.baseURL}${endpoint}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });

                const isJson = response.headers.get('content-type')?.includes('application/json');
                if (!response.ok) {
                    if (isJson) {
                        return await response.json();
                    }
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return isJson ? await response.json() : await response.text();
            } finally {
                this.inflightRequests.delete(cacheKey);
            }
        })();

        this.inflightRequests.set(cacheKey, requestPromise);
        return requestPromise;
    }

    /**
     * Shared Endpoints
     */
    getOrderList(payload) {
        return this._post('order-list', payload);
    }

    getAccountStatement(payload) {
        return this._post('account-statement', payload);
    }

    getTSEDealers(payload) {
        return this._post('tse-level-dealers', payload);
    }

    generateInvoicePDF(payload) {
        return this._post('invoice-pdf-generate', payload);
    }

    getShipToPartyList(payload) {
        return this._post('get-ship-to-party-list', payload);
    }

    createOrder(payload) {
        return this._post('create-order', payload);
    }

    /**
     * Utility: Number Formatting (Million/Standard)
     */
    formatIndianNumber(num, decimalPlaces = 2) {
        if (!num || isNaN(parseFloat(num))) return num || '0';
        const parts = parseFloat(num).toFixed(decimalPlaces).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return parts.join('.');
    }

    formatNumber(num, decimalPlaces = 2) {
        if (!num || isNaN(parseFloat(num))) return num || '0';
        const parts = parseFloat(num).toFixed(decimalPlaces).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return parts.join('.');
    }
}

// Global instance
window.APIManager = new APIService();
